import { config } from '../config';

/**
 * A sphere-watch PASSWORD sign-in, held in this browser -- the other way in
 * beside Google (auth/firebase.ts).
 *
 * Two things are kept, in localStorage, and they are different on purpose:
 *
 *   the SESSION: the bearer token the API was handed once the password and
 *   the emailed code were right. It is what api/client.ts sends, it ends after
 *   the server's session hours, and signing out ends it on the server too.
 *
 *   REMEMBERED BROWSERS: per address, the token that lets this browser skip
 *   the emailed code -- never the password -- until it expires. It outlives
 *   signing out, because remembering is about the browser, not the visit.
 *
 * Firebase keeps a Google sign-in in this same browser storage, so a password
 * session is no more exposed here than a Google one already was.
 *
 * Every storage call is guarded: a private window or blocked site data
 * throws, and the console must still sign in -- just without remembering.
 */
export interface PasswordSession {
  token: string;
  email: string;
  expiresAt: string;
}

interface Remembered {
  token: string;
  expiresAt: string;
}

export interface CodeSent {
  challenge: string;
  /** The address, masked: `v•••a@limosphere.com`. */
  sentTo: string;
  expiresAt: string;
  /** How long "remember this browser" lasts on this server. */
  rememberDays: number;
}

interface SignedIn {
  session: PasswordSession;
  browser: Remembered | null;
}

/** A refusal from the sign-in API: `code` is what to branch on, the message what to show. */
export class SignInError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public weaknesses: { key: string; label: string }[] = [],
  ) {
    super(message);
  }
}

const SESSION_KEY = 'watch.session';
const BROWSERS_KEY = 'watch.rememberedBrowsers';

function read<T>(key: string): T | null {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function write(key: string, value: unknown) {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Nothing kept; this tab still works until it is closed.
  }
}

const unexpired = <T extends { expiresAt: string }>(value: T | null | undefined): T | null =>
  value && typeof value.expiresAt === 'string' && new Date(value.expiresAt).getTime() > Date.now()
    ? value
    : null;

let current = unexpired(read<PasswordSession>(SESSION_KEY));
let ended: string | null = null;
const listeners = new Set<() => void>();
const notify = () => {
  for (const listener of listeners) listener();
};

/** The live password session, or null. */
export const passwordSession = () => current;

/** Why the last password session ended, when the server ended it -- for the sign-in screen to say. */
export const sessionEnded = () => ended;

/**
 * Why a sign-in ended, for the sign-in screen to say, when it was not a
 * password session -- a Google one the server refused, or one signed out for
 * sitting idle (auth/idle.ts). Set before signing out, so it is there when
 * the sign-in screen draws.
 */
export const noteSessionEnded = (reason: string) => {
  ended = reason;
};

/** Said once: the sign-in screen clears it when it has shown it, so it does not come back later. */
export const clearSessionEnded = () => {
  ended = null;
};

export function setPasswordSession(next: PasswordSession | null, reason: string | null = null) {
  current = next;
  ended = next ? null : reason;
  write(SESSION_KEY, next);
  notify();
}

export function onPasswordSession(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

// Signing in or out in another tab is this tab's too.
window.addEventListener('storage', (event) => {
  if (event.key !== SESSION_KEY) return;
  current = unexpired(read<PasswordSession>(SESSION_KEY));
  notify();
});

const remembered = () => read<Record<string, Remembered>>(BROWSERS_KEY) ?? {};

const rememberedFor = (email: string) => unexpired(remembered()[email.trim().toLowerCase()])?.token;

function rememberBrowser(email: string, browser: Remembered) {
  const all = remembered();
  // Expired entries go whenever the list is written.
  for (const [address, entry] of Object.entries(all)) if (!unexpired(entry)) delete all[address];
  all[email.trim().toLowerCase()] = browser;
  write(BROWSERS_KEY, all);
}

function forgetBrowser(email: string) {
  const all = remembered();
  delete all[email.trim().toLowerCase()];
  write(BROWSERS_KEY, all);
}

async function post<T>(path: string, body: unknown, bearer?: string): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`${config.authRoot}${path}`, {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        ...(bearer ? { authorization: `Bearer ${bearer}` } : {}),
      },
      body: JSON.stringify(body ?? {}),
    });
  } catch {
    throw new SignInError(0, 'network', 'sphere-watch could not be reached. Check your connection and try again.');
  }
  let payload: Record<string, unknown> | null = null;
  try {
    payload = (await response.json()) as Record<string, unknown>;
  } catch {
    throw new SignInError(response.status, 'unreadable', `sphere-watch answered ${response.status}, unreadably.`);
  }
  if (!response.ok || payload?.ok === false) {
    throw new SignInError(
      response.status,
      String(payload?.code ?? response.status),
      String(payload?.error ?? `sphere-watch answered ${response.status}.`),
      Array.isArray(payload?.weaknesses) ? (payload.weaknesses as { key: string; label: string }[]) : [],
    );
  }
  return payload as T;
}

function signedIn(answer: SignedIn) {
  if (answer.browser) rememberBrowser(answer.session.email, answer.browser);
  setPasswordSession(answer.session);
}

/**
 * Step one. Signed in already when this browser was remembered for the
 * address; otherwise a code is on its way and the challenge comes back.
 */
export async function passwordLogin(email: string, password: string): Promise<CodeSent | null> {
  const browser = rememberedFor(email);
  const answer = await post<Partial<SignedIn> & { code?: CodeSent }>('/login', { email, password, browser });
  if (answer.session) {
    signedIn(answer as SignedIn);
    return null;
  }
  // A remembered browser that still asked for a code is one the server has
  // ended -- a new password, access turned off, or simply its days are up.
  if (browser) forgetBrowser(email);
  return answer.code ?? null;
}

export async function submitCode(challenge: string, code: string, remember: boolean) {
  signedIn(await post<SignedIn>('/code', { challenge, code, remember }));
}

export const resendCode = (challenge: string) => post<{ expiresAt: string }>('/code/resend', { challenge });

export const askPasswordCode = (email: string) => post<CodeSent>('/password/code', { email });

export async function setPassword(args: {
  email: string;
  challenge: string;
  code: string;
  password: string;
  remember: boolean;
}) {
  signedIn(await post<SignedIn>('/password', args));
}

/**
 * End the password session here and on the server. The server half is best
 * effort. `reason` is what the sign-in screen says, when it was not the
 * person's own choice.
 */
export async function endPasswordSession(reason: string | null = null) {
  const session = current;
  setPasswordSession(null, reason);
  if (session) await post('/logout', {}, session.token).catch(() => {});
}
